import * as yup from 'yup';
import { ArrowLeft, Bell, Check } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';

import { Event } from '../Checkout/helpers';
import { SectionTitle, TitleBar } from '../Common/Layout';
import { emailRegex, getServerErrors } from '../../common/helpers';
import { useEvent } from '../Common/EventProvider';
import EmailInput from '../../common/ParticipantAttributes/Attributes/EmailInput';
import SaveInterestMutation from './SaveInterestMutation';
import UI from '../../common/UI';
import config from '../config';
import useAutoScrollToSection from '../Common/useAutoScrollToSection';
import useDocumentTitle from '../../common/useDocumentTitle';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';
import useParticipantExistenceCheck from '../../common/useParticipantExistenceCheck';
import useValidation from '../../common/useValidation';

const InterestPage = () => {
  const { t } = useTranslation();
  const { event } = useEvent();

  useDocumentTitle([t('interest_list.newsletter'), event.title, t('common:atleta')]);

  useAutoScrollToSection();

  const [success, setSuccess] = useState(false);

  const enabled = event.enable_interest_list && event.interest_list;

  return (
    <>
      <UI.Div>
        <TitleBar>
          <UI.Div />
          <UI.Div>
            {t('interest_list.newsletter')}
          </UI.Div>
          <UI.Div />
        </TitleBar>

        <UI.Card sc={{ hasPanels: true, active: true }}>
          {success && (
            <UI.FadeIn>
              <UI.FormGrid sc={{ textAlign: 'center' }}>
                <UI.Badge sc={{ size: 48, brand: 'success' }} style={{ margin: '0 auto' }}>
                  <UI.Icon sc={{ size: 2.5 }}>
                    <Check />
                  </UI.Icon>
                </UI.Badge>

                <SectionTitle>
                  {t('interest_list.subscribe_success')}
                </SectionTitle>

                <UI.Div>
                  <UI.LinkButton to={`/${event.id}`} sc={{ outline: true }} resetScrollPosition={false}>
                    <UI.Icon>
                      <ArrowLeft />
                    </UI.Icon>
                    {' '}
                    {t('back')}
                  </UI.LinkButton>
                </UI.Div>
              </UI.FormGrid>
            </UI.FadeIn>
          )}
          {!success && (
            <UI.FadeIn>
              {enabled && <InterestListForm event={event} onSuccess={() => setSuccess(true)} />}
              {!enabled && (
                <UI.Div sc={{ textAlign: 'center' }}>
                  {t('interest_list.disabled_message')}
                </UI.Div>
              )}
            </UI.FadeIn>
          )}
        </UI.Card>
      </UI.Div>
    </>
  );
};

interface InterestListFormProps {
  event: Event;
  onSuccess: () => unknown;
}

const InterestListForm = ({ event, onSuccess }: InterestListFormProps) => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const form = useForm({
    values: {
      email: '',
      first_name: '',
      last_name: '',
      subscribe: true,
    },
  });

  const interestList = event.interest_list;

  const { participantExists, loading: checkingParticipant } = useParticipantExistenceCheck({
    participant: form.values,
  });

  const schema = yup.object().shape({
    email: yup.string().required().matches(emailRegex, { excludeEmptyString: true, name: 'email' }),
    first_name: participantExists ? yup.mixed().nullable() : yup.string().required(),
    last_name: participantExists ? yup.mixed().nullable() : yup.string().required(),
    subscribe: yup.bool().required().oneOf([true]),
  });

  const [subscribe, { error, loading }] = useMutation(SaveInterestMutation, {
    variables: {
      input: {
        interest_list: interestList.id,
        email: form.values.email,
        first_name: form.values.first_name,
        last_name: form.values.last_name,
      },
    },
    onCompleted: () => {
      onSuccess();
    },
  });

  const { valid, errors } = useValidation({
    schema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => subscribe()}>
      <UI.FormGrid>
        <SectionTitle>
          {interestList.title}
        </SectionTitle>

        <EmailInput
          value={form.values}
          onChange={(value) => form.set((contact) => ({ ...contact, ...value }))}
          onBlur={(key) => form.setTouched(key, true)}
          touched={form.touched}
          errors={errors}
          required
        />

        {!participantExists && (
          <UI.FormGrid sc={{ columns: ['1fr 1fr'] }}>
            <UI.InputGroup sc={{ valid: !errors.first_name, touched: form.touched.first_name }}>
              <UI.InputLabel htmlFor="FirstName">
                {t('common:participant_attributes.first_name')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.DebouncedInput {...form.text('first_name')} maxLength={100} id="FirstName" />
              <UI.ErrorMessages
                attribute={t('common:participant_attributes.first_name')}
                errors={errors.first_name}
              />
            </UI.InputGroup>
            <UI.InputGroup sc={{ valid: !errors.last_name, touched: form.touched.last_name }}>
              <UI.InputLabel htmlFor="LastName">
                {t('common:participant_attributes.last_name')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.DebouncedInput {...form.text('last_name')} maxLength={100} id="LastName" />
              <UI.ErrorMessages
                attribute={t('common:participant_attributes.last_name')}
                errors={errors.last_name}
              />
            </UI.InputGroup>
          </UI.FormGrid>
        )}

        <UI.InputGroup>
          <UI.Checkbox {...form.checkbox('subscribe')}>
            {interestList.label}
          </UI.Checkbox>
        </UI.InputGroup>

        <UI.HR />

        <UI.Div>
          <Trans
            i18nKey="common:terms.terms_notice"
            values={{
              action: t('interest_list.subscribe'),
            }}
          >
            <UI.A href={config.termsUrl[locale]} target="_blank" rel="noopener noreferer" />
          </Trans>
        </UI.Div>

        <UI.Button
          type="submit"
          sc={{ brand: 'secondary', block: true, loading }}
          disabled={loading || !valid || checkingParticipant}
        >
          <UI.Icon>
            <Bell />
          </UI.Icon>
          {' '}
          {t('interest_list.subscribe')}
        </UI.Button>
      </UI.FormGrid>
    </UI.Form>
  );
};

export default InterestPage;
